import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import fastDeepEqual from 'fast-deep-equal/es6';
import { cloneDeep } from 'lodash-es';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, find } from 'rxjs/operators';
import { GetUserDto } from '../../dtos/getUserDto.type';
import { UpdateUserDto } from '../../dtos/updateUserDto.type';
import { IUserGeneralInformation } from '../../interfaces/user-general-information.interface';
import { AuthProvider } from '../../provider/auth.provider';

@UntilDestroy()
@Component({
    selector: 'aumico-user-general-information-page',
    templateUrl: './user-general-information.page.html',
    styleUrls: ['./user-general-information.page.scss'],
})
export class UserGeneralInformationPage implements OnInit {
    user: IUserGeneralInformation;

    private modifiedUser: IUserGeneralInformation;
    private dataChanges$: BehaviorSubject<IUserGeneralInformation> = new BehaviorSubject(null);

    @Input() supportedLocales: string[] = [];

    @Input() proceedButtonEvent$: Observable<void>;
    @Input() save$: Observable<void>;
    @Input() countries: INameId[] = [];
    @Input() userCountry: string;
    @Output() userGeneralInformationChanges = new EventEmitter<IUserGeneralInformation>();
    @Output() initialUserGeneralInformation = new EventEmitter<IUserGeneralInformation>();

    constructor(private authProvider: AuthProvider) {}

    ngOnInit(): void {
        this.authProvider
            .getCurrentUser$()
            .pipe(
                find((user) => user != null),
                distinctUntilChanged((prev, curr) => fastDeepEqual(prev, curr)),
                untilDestroyed(this),
            )
            .subscribe((user: GetUserDto) => this.handleGetCurrentUserResponse(user));

        this.authProvider.dispatchGetCurrentUser();

        this.proceedButtonEvent$.pipe(untilDestroyed(this)).subscribe(() => this.updateUser());

        this.save$.pipe(untilDestroyed(this)).subscribe(() => this.updateUser());

        this.dataChanges$
            .pipe(
                distinctUntilChanged((previous, current) => fastDeepEqual(previous, current)),
                untilDestroyed(this),
            )
            .subscribe((userGeneralInformationChanges) => {
                this.userGeneralInformationChanges.emit(userGeneralInformationChanges);
            });
    }

    private handleGetCurrentUserResponse(user: GetUserDto): void {
        this.user = this.mapGetUserDtoToIUserGeneralInformation(user);
        this.initialUserGeneralInformation.emit(this.user);
        this.dataChanges$.next(this.user);
    }

    private mapGetUserDtoToIUserGeneralInformation(user: GetUserDto): IUserGeneralInformation {
        const country: INameId | undefined = this.countries.find((country) => country.id === this.userCountry);
        return {
            salutation: user.title,
            name: user.firstName,
            surname: user.lastName,
            email: user.email,
            phone: user.phoneNumber,
            language: user.locale,
            country,
        };
    }
    modifyUserProfileInformation(user: IUserGeneralInformation): void {
        this.modifiedUser = user;
        this.dataChanges$.next(this.modifiedUser);
    }

    private updateUser(): void {
        const modifiedUserClone = cloneDeep<IUserGeneralInformation>(this.modifiedUser);
        const userToSave = this.mapIUserGeneralInformationToUpdateUserDTO(modifiedUserClone);
        this.authProvider.dispatchUpdateCurrentUser(userToSave);
    }

    private mapIUserGeneralInformationToUpdateUserDTO(user: IUserGeneralInformation): UpdateUserDto {
        return {
            firstName: user.name,
            lastName: user.surname,
            title: user.salutation,
            phoneNumber: user.phone,
            locale: user.language,
            countryISO31661Alpha2Code: user.country.id,
        };
    }
}
